import { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Lock, User, Home } from 'lucide-react';
import { toast } from 'sonner';

const RECAPTCHA_SITE_KEY = '6LdIjVAsAAAAANn94t3x-PgqpKP2ESWhwD8nImst';

// Get reCAPTCHA token
const getRecaptchaToken = async () => {
  return new Promise((resolve, reject) => {
    if (window.grecaptcha && window.grecaptcha.enterprise) {
      window.grecaptcha.enterprise.ready(async () => {
        try {
          const token = await window.grecaptcha.enterprise.execute(RECAPTCHA_SITE_KEY, { action: 'LOGIN' });
          resolve(token);
        } catch (error) {
          console.error('reCAPTCHA error:', error);
          reject(error);
        }
      });
    } else {
      reject(new Error('reCAPTCHA not loaded'));
    }
  });
};

const AdminLoginPage = () => {
  const navigate = useNavigate();
  const { login, register, isAuthenticated } = useAuth();
  const [isLogin, setIsLogin] = useState(true);
  const [formData, setFormData] = useState({ username: '', password: '' });
  const [loading, setLoading] = useState(false);

  // Show reCAPTCHA badge on this page
  useEffect(() => {
    document.body.classList.add('show-recaptcha');
    return () => {
      document.body.classList.remove('show-recaptcha');
    };
  }, []);

  // If we're already authenticated (e.g. user opened /admin/login while logged in),
  // bounce to the dashboard. Doing this in an effect — instead of inline during
  // render — avoids React's "setState during render" warning from BrowserRouter.
  useEffect(() => {
    if (isAuthenticated) {
      navigate('/admin', { replace: true });
    }
  }, [isAuthenticated, navigate]);

  if (isAuthenticated) {
    return null;
  }

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      // Get reCAPTCHA token
      const recaptchaToken = await getRecaptchaToken();
      
      if (isLogin) {
        await login(formData.username, formData.password, recaptchaToken);
        toast.success('Inloggad!');
      } else {
        await register(formData.username, formData.password, recaptchaToken);
        toast.success('Konto skapat!');
      }
      navigate('/admin');
    } catch (error) {
      // Log the real error to the console so admins can diagnose deployment issues
      // (CORS, reCAPTCHA misconfig, wrong credentials, server down, etc.).
      console.error('[admin-login] failed:', error?.response?.status, error?.response?.data, error?.message);

      if (error.message === 'reCAPTCHA not loaded') {
        toast.error('Säkerhetsverifiering misslyckades. Ladda om sidan.');
        return;
      }

      const status = error?.response?.status;
      const detail = error?.response?.data?.detail;

      if (status === 400 && typeof detail === 'string' && detail.toLowerCase().includes('recaptcha')) {
        // Surface the real reCAPTCHA-side error instead of the generic credentials toast
        toast.error(`reCAPTCHA: ${detail}`);
      } else if (status === 401) {
        toast.error(isLogin ? 'Ogiltiga inloggningsuppgifter' : 'Registrering misslyckades');
      } else if (status === 403) {
        toast.error(detail || 'Kontot är inaktiverat');
      } else if (status >= 500) {
        toast.error('Serverfel. Kontrollera serverloggar.');
      } else if (!error?.response) {
        // Network / CORS error — no HTTP response received at all
        toast.error('Nätverksfel — kunde inte nå servern (CORS eller anslutning?). Se konsolen.');
      } else {
        toast.error(detail || (isLogin ? 'Ogiltiga inloggningsuppgifter' : 'Registrering misslyckades'));
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-yellow-100 via-amber-50 to-green-100 flex items-center justify-center p-4">
      {/* Back to Website Button */}
      <Link
        to="/"
        className="absolute top-4 left-4 flex items-center gap-2 px-4 py-2 bg-white/80 hover:bg-white rounded-lg shadow-md transition-all text-gray-700 hover:text-yellow-600"
        data-testid="back-to-website-login"
      >
        <Home className="w-5 h-5" />
        <span className="font-medium">Tillbaka till webbplats</span>
      </Link>

      <Card className="w-full max-w-md border-0 shadow-2xl">
        <CardHeader className="text-center space-y-4 pb-8">
          <img 
            src="/favicon-large.png" 
            alt="Vår Fru av Fatima" 
            className="w-20 h-20 rounded-full mx-auto shadow-lg"
          />
          <CardTitle className="text-3xl font-serif font-bold text-gray-900">
            {isLogin ? 'Admin Inloggning' : 'Skapa Admin'}
          </CardTitle>
          <p className="text-gray-600">Katolska Församlingen i Blekinge</p>
        </CardHeader>
        <CardContent>
          <form onSubmit={handleSubmit} className="space-y-6" data-testid="admin-login-form">
            <div>
              <label htmlFor="username" className="block text-sm font-medium text-gray-700 mb-2">
                Användarnamn
              </label>
              <div className="relative">
                <User className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" />
                <Input
                  id="username"
                  type="text"
                  required
                  value={formData.username}
                  onChange={(e) => setFormData({ ...formData, username: e.target.value })}
                  className="pl-10"
                  placeholder="admin"
                  data-testid="admin-username"
                />
              </div>
            </div>

            <div>
              <label htmlFor="password" className="block text-sm font-medium text-gray-700 mb-2">
                Lösenord
              </label>
              <div className="relative">
                <Lock className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" />
                <Input
                  id="password"
                  type="password"
                  required
                  value={formData.password}
                  onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                  className="pl-10"
                  placeholder="••••••••"
                  data-testid="admin-password"
                />
              </div>
            </div>

            <Button
              type="submit"
              disabled={loading}
              className="w-full bg-yellow-500 hover:bg-yellow-600 text-gray-900 font-semibold"
              size="lg"
              data-testid="admin-submit"
            >
              {loading ? 'Laddar...' : isLogin ? 'Logga in' : 'Skapa konto'}
            </Button>
          </form>

          <div className="mt-6 text-center">
            <button
              onClick={() => setIsLogin(!isLogin)}
              className="text-sm text-blue-600 hover:underline"
              data-testid="toggle-auth-mode"
            >
              {isLogin ? 'Behöver du skapa ett konto?' : 'Har du redan ett konto?'}
            </button>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default AdminLoginPage;